import {
  Box,
  LuxRadioButton,
  LuxSelect,
  LuxTextField,
  LuxTypography,
  useMediaQuery,
  useTheme,
} from '@gemini/fe-ds-core';
import { yupResolver } from '@hookform/resolvers/yup';
import useNoopClickableGuard from '@hooks/useNoopClickableGuard';
import { useOnlineBookingStore } from '@store/onlineBookingStore';
import { MobileBreakpoint } from '@utils/consts/breakpoints.consts';
import { StaticValues } from '@utils/enums/StaticValues.enum';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, Resolver, useForm, useWatch } from 'react-hook-form';

import {
  CalendarInsuranceFormData,
  CalendarInsuranceFormProps,
} from './CalendarInsuranceForm.interface';
import { calendarInsuranceFormSchema } from './CalendarInsuranceForm.schema';
import { toCalendarInsuranceDefaultValues } from './CalendarInsuranceForm.utils';
import { getInsuranceFieldErrorProps } from '../InsurancesForm.utils';

const getInputValue = (valueOrEvent: unknown) => {
  if (
    valueOrEvent &&
    typeof valueOrEvent === 'object' &&
    'target' in valueOrEvent
  ) {
    return (
      (valueOrEvent as { target?: { value?: string } }).target?.value || ''
    );
  }

  return typeof valueOrEvent === 'string' ? valueOrEvent : '';
};

const CalendarInsuranceForm = ({
  currentIndex,
  insurances,
  initialInsurance,
  isVisible = true,
  submitAttemptCount = 0,
  onValidate,
  onChange,
  onCommit,
}: CalendarInsuranceFormProps) => {
  // #region Hooks
  const [t] = useTranslation();
  const brandName = useOnlineBookingStore(state => state.brandName);
  const patientId = useOnlineBookingStore(state => state.infoUser?.patientId);
  const canPrefillInsurance = currentIndex === 0 && !!patientId;
  const isMobile = useMediaQuery(`(max-width:${MobileBreakpoint}px)`);
  const theme = useTheme();
  const formConfigs = useOnlineBookingStore(state => state.formConfigs);
  const insurancePlanStatus =
    formConfigs?.patientDetails?.primaryInsuranceDetails?.submitInsurancePlan;
  const insuranceIdStatus =
    formConfigs?.patientDetails?.primaryInsuranceDetails?.insuranceId;
  const onChangeRef = useRef(onChange);
  const onCommitRef = useRef(onCommit);
  const [showSubmittedErrors, setShowSubmittedErrors] = useState(false);
  const validationMode = brandName ? 'onBlur' : 'onChange';
  const clickableGuardOptions = useMemo(
    () => ({
      forceSelector: '*',
      hideRedundantSelectLabels: true,
    }),
    [],
  );
  const clickableGuardRef = useNoopClickableGuard<HTMLDivElement>(
    clickableGuardOptions,
  );

  const {
    control,
    setValue,
    getValues,
    trigger,
    watch,
    formState: { isValid },
  } = useForm<CalendarInsuranceFormData>({
    mode: validationMode,
    reValidateMode: validationMode,
    resolver: yupResolver(
      calendarInsuranceFormSchema(insurancePlanStatus, insuranceIdStatus),
    ) as Resolver<CalendarInsuranceFormData>,
    defaultValues: toCalendarInsuranceDefaultValues(initialInsurance),
  });

  const insurancePlan = useWatch({
    control,
    name: 'insurance.plan',
  });
  const insuranceId = useWatch({
    control,
    name: 'insurance.id',
  });
  // #endregion Hooks

  // #region Effects
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  useEffect(() => {
    trigger();
  }, [currentIndex, insuranceIdStatus, insurancePlanStatus, trigger]);

  useEffect(() => {
    if (submitAttemptCount === 0 || !isVisible) {
      setShowSubmittedErrors(false);
      return;
    }

    void trigger(['insurance.plan', 'insurance.id', 'insurance.other']).then(
      () => {
        setShowSubmittedErrors(true);
      },
    );
  }, [isVisible, submitAttemptCount, trigger]);

  useEffect(() => {
    onValidate(isValid);
  }, [isValid, onValidate]);

  useEffect(() => {
    const subscription = watch(value => {
      onChangeRef.current?.(value as CalendarInsuranceFormData);
    });

    return () => subscription.unsubscribe();
  }, [watch]);

  useEffect(() => {
    if (
      !canPrefillInsurance ||
      insurancePlanStatus === StaticValues.FIELD_STATUS_HIDDEN ||
      insuranceIdStatus === StaticValues.FIELD_STATUS_HIDDEN ||
      getValues('insurance.plan') ||
      getValues('insurance.id')
    ) {
      return;
    }

    const firstInsuranceId = insurances?.[0]?.insuranceId?.toString();
    if (!firstInsuranceId) return;

    setValue('insurance.plan', StaticValues.YES, { shouldValidate: true });
    setValue('insurance.id', firstInsuranceId, { shouldValidate: true });
  }, [
    canPrefillInsurance,
    getValues,
    insuranceIdStatus,
    insurancePlanStatus,
    insurances,
    setValue,
  ]);

  useEffect(
    () => {
      return () => {
        onCommitRef.current?.(getValues());
      };
    },
    [
      /* getValues */
    ],
  );
  // #endregion Effects

  // #region Memos
  const planList = useMemo(
    () => [
      {
        label: t('fieldsLabel.yes') as string,
        value: StaticValues.YES,
      },
      {
        label: t('fieldsLabel.no') as string,
        value: StaticValues.NO,
      },
    ],
    [t],
  );

  const insuranceList = useMemo(
    () =>
      (insurances || []).map(insurance => ({
        label: insurance.name,
        value: insurance.insuranceId.toString(),
        isBold: insurance.hasPolicy,
      })),
    [insurances],
  );
  // #endregion Memos

  // #region Constants
  const SHOW_INSURANCE_PLAN =
    insurancePlanStatus !== StaticValues.FIELD_STATUS_HIDDEN;
  const RENDER_INSURANCE_DEPENDENCIES =
    SHOW_INSURANCE_PLAN &&
    insurancePlan === StaticValues.YES &&
    insuranceIdStatus !== StaticValues.FIELD_STATUS_HIDDEN;
  const INSURANCE_PLAN_REQUIRED =
    insurancePlanStatus !== StaticValues.FIELD_STATUS_OPTIONAL;
  const INSURANCE_ID_REQUIRED =
    insuranceIdStatus !== StaticValues.FIELD_STATUS_OPTIONAL;

  const hideSubmittedErrorsAfterChange = () => {
    if (showSubmittedErrors) {
      setShowSubmittedErrors(false);
    }
  };

  const getSubmittedInsuranceFieldErrorProps = (
    fieldState: Parameters<typeof getInsuranceFieldErrorProps>[0],
  ) => {
    if (!showSubmittedErrors) {
      return {
        error: false,
        helperText: undefined,
      };
    }

    return getInsuranceFieldErrorProps(fieldState, validationMode, true);
  };
  // #endregion Constants

  // #region JSX
  return (
    <Box
      ref={clickableGuardRef}
      padding="0.5rem 1rem 1rem 1rem"
      display={isVisible ? 'block' : 'none'}
    >
      <Controller
        name="insurance.plan"
        control={control}
        render={({ field, fieldState }) => {
          if (!SHOW_INSURANCE_PLAN) {
            return <></>;
          }

          const { error, helperText } =
            getSubmittedInsuranceFieldErrorProps(fieldState);

          return (
            <>
              <LuxRadioButton
                name={`scheduleInsurancePlan_${currentIndex}`}
                direction="horizontal"
                required={INSURANCE_PLAN_REQUIRED}
                standalone
                label={t('fieldsLabel.insurancePlan') as string}
                options={planList}
                value={field.value || ''}
                noHelper
                onChange={value => {
                  hideSubmittedErrorsAfterChange();
                  field.onChange(value);
                  if (value === StaticValues.NO) {
                    setValue('insurance.id', '', { shouldValidate: true });
                    setValue('insurance.other', '', { shouldValidate: true });
                  }
                }}
              />
              {error && helperText && (
                <LuxTypography
                  variant="textXs"
                  marginTop="0.25rem"
                  style={{
                    color: theme.palette.LUX.obTokens.textColorFunctionalError,
                  }}
                >
                  {helperText}
                </LuxTypography>
              )}
            </>
          );
        }}
      />
      {RENDER_INSURANCE_DEPENDENCIES && (
        <Box
          display="grid"
          gridTemplateColumns={isMobile ? '1fr' : '1fr 1fr'}
          gap="1rem"
        >
          <Controller
            name="insurance.id"
            control={control}
            render={({ field, fieldState }) => (
              <LuxSelect
                {...field}
                label={t('fieldsLabel.insuranceName') as string}
                customOptions={insuranceList}
                usePropsOptions
                required={INSURANCE_ID_REQUIRED}
                value={field.value || ''}
                {...getSubmittedInsuranceFieldErrorProps(fieldState)}
                onChange={valueOrEvent => {
                  hideSubmittedErrorsAfterChange();
                  field.onChange(valueOrEvent);
                  const value = getInputValue(valueOrEvent);
                  if (value !== StaticValues.OTHER_OPTION) {
                    setValue('insurance.other', '', {
                      shouldValidate: true,
                    });
                  }
                }}
              />
            )}
          />

          {insuranceId === StaticValues.OTHER_OPTION && (
            <Controller
              name="insurance.other"
              control={control}
              render={({ field, fieldState }) => (
                <LuxTextField
                  {...field}
                  label={t('fieldsLabel.otherInsurance') as string}
                  required={INSURANCE_ID_REQUIRED}
                  value={field.value || ''}
                  maxLength={255}
                  {...getSubmittedInsuranceFieldErrorProps(fieldState)}
                  onChange={event => {
                    hideSubmittedErrorsAfterChange();
                    field.onChange(event);
                  }}
                />
              )}
            />
          )}
        </Box>
      )}
    </Box>
  );
  // #endregion JSX
};

export default memo(CalendarInsuranceForm);
